import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmt, fmtTime } from "./meta";

const Counts = ({ c = {} }) => (
  <span className="tabular-nums" data-testid="bsp-apply-counts">
    <b className="text-emerald-700">{fmt(c.applied)}</b> applied · <b className="text-slate-600">{fmt(c.skipped)}</b> skipped · <b className={c.failed ? "text-red-700" : "text-slate-400"}>{fmt(c.failed)}</b> failed
  </span>
);

/** Latest apply for the shown run; polls while it runs and reports back when it ends. */
export function ApplyStatus({ run, applyId, onFinished, onMeta }) {
  const [a, setA] = useState(null);
  useEffect(() => {
    api.get(`/billing-sync/preview/runs/${run.id}/applies`).then(({ data }) => {
      onMeta({ canApply: data.can_apply, running: data.applies[0]?.status === "running" });
      setA(data.applies[0] || null);
    }).catch(() => {});
  }, [run.id, applyId, onMeta]);
  useEffect(() => {
    if (a?.status !== "running") return undefined;
    const t = setInterval(() => api.get(`/billing-sync/preview/applies/${a.id}`).then(({ data }) => {
      setA(data);
      if (data.status === "running") return;
      onMeta((m) => ({ ...m, running: false }));
      if (data.status === "done") toast.success(`${data.type === "undo" ? "Undo finished" : "Stax Bill updated"} — ${fmt(data.counts?.applied)} applied, ${fmt(data.counts?.skipped)} skipped, ${fmt(data.counts?.failed)} failed`);
      else toast.error(`The apply stopped: ${data.error}`);
      onFinished();
    }).catch(() => {}), 2000);
    return () => clearInterval(t);
  }, [a?.id, a?.status, onFinished, onMeta]);
  if (!a) return null;
  const pct = a.total ? Math.round((a.done / a.total) * 100) : 0;
  if (a.status === "running") return (
    <div className="rounded-lg border border-emerald-200 bg-white px-5 py-3" data-testid="bsp-apply-running">
      <div className="flex items-center gap-2 text-sm font-medium text-slate-800"><Loader2 className="h-4 w-4 animate-spin text-emerald-600" />
        {a.type === "undo" ? "Undoing in Stax Bill" : "Applying to Stax Bill"} — {fmt(a.done)} of {fmt(a.total)}<span className="ml-auto text-xs font-normal text-slate-500"><Counts c={a.counts} /></span></div>
      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500 transition-[width] duration-700" style={{ width: `${Math.max(pct, 3)}%` }} /></div>
      <p className="mt-1.5 text-[11px] text-slate-500">Adds go in one call per subscription and Stax Bill can take half a minute per call. You can leave this page.</p>
    </div>);
  const Icon = a.status === "done" ? CheckCircle2 : XCircle;
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-lg border px-4 py-2 text-xs ${a.status === "done" ? "border-slate-200 bg-white text-slate-600" : "border-red-200 bg-red-50 text-red-800"}`} data-testid="bsp-apply-last">
      <Icon className={`h-4 w-4 ${a.status === "done" ? "text-emerald-600" : "text-red-600"}`} />
      Last {a.type === "undo" ? "undo" : "apply"} {fmtTime(a.finished_at || a.started_at)} by {a.started_by_name}: <Counts c={a.counts} />
      {a.error && <span>· {a.error}</span>}
      <Link to={`/billing-sync/apply-history/${a.id}`} className="ml-auto font-medium text-emerald-700 hover:underline" data-testid="bsp-apply-history-link">Row by row →</Link>
    </div>);
}
